import type { FC } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { Select } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";

import type { SelectOptionType } from "@jti/fe-common";

type RegionFieldProps = {
  regions: SelectOptionType[];
  fieldName?: (name: string) => string;
  required?: boolean;
  isIconHidden?: boolean;
  isLoading?: boolean;
};

const REGION_CODE = "regionCode";

export const RegionField: FC<RegionFieldProps> = ({
  regions,
  fieldName = (name) => name,
  required = true,
  isIconHidden = true,
  isLoading = false,
}) => {
  const { control } = useFormContext();
  const { __ } = useTranslations();

  return (
    <Controller
      name={fieldName(REGION_CODE)}
      control={control}
      rules={{
        required: { value: required, message: __("libs.ui.address_fields.region.validation.field_required") },
      }}
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          id={__("libs.ui.address_fields.region.label")}
          options={regions}
          hasLeadingIcon={!isIconHidden}
          showRequiredAsterisk={required}
          required={required}
          onChange={(selectedOption) => field.onChange(selectedOption?.value)}
          value={field.value ? (regions.find((opt) => opt.value === field.value) ?? null) : null}
          dataTestId={REGION_CODE}
          placeholder={__("libs.ui.address_fields.region.placeholder")}
          label={__("libs.ui.address_fields.region.label")}
          aria-label={__("libs.ui.address_fields.region.label")}
          noOptionsMessage={() => __("libs.ui.address_fields.region.no_options")}
          isHelperTextVisible={!!error}
          isError={!!error}
          errorText={error?.message}
          isLoading={isLoading}
        />
      )}
    />
  );
};
